import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Heart, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useCart } from '@/context/CartContext';
import { useAccessoriGroups } from '@/hooks/useAccessoriGroups';
import { useWishlist } from '@/context/WishlistContext';

const CATEGORIES = ['Anelli', 'Collane', 'Bracciali', 'Orecchini', 'Pendenti', 'Orologi'];
// Per il momento solo D1 Milano. Brand completi (ripristino futuro): 'Rolex', 'Cartier', 'Bulgari', 'Tudor', 'D1 Milano'
const WATCH_BRANDS = ['D1 Milano'];
const ARGENTO_SUBS = ['Collane', 'Bracciali', 'Anelli', 'Orecchini'];

const navLinks = [
  { href: '/collezione',   label: 'Collezione' },
  { href: '/argento',      label: 'Argento' },
  { href: '/accessori',    label: 'Accessori' },
  { href: '/chi-siamo',    label: 'Chi Siamo' },
  { href: '/appuntamento', label: 'Appuntamento' },
  { href: '/contatti',     label: 'Contatti' },
];

/**
 * Header — editorial nav, token-driven.
 * No hardcoded colors. Transparent on Home hero, ivory glass on scroll.
 * Focus ring (bordeaux) comes from global :focus-visible.
 */
export default function Header() {
  const location = useLocation();
  const { cartItemsCount } = useCart();
  const { wishlistCount } = useWishlist();
  const [isOpen, setIsOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  const accessoriGroups = useAccessoriGroups();
  const subMenus = {
    '/argento': { subs: ARGENTO_SUBS, id: 'argento' },
    '/accessori': { subs: accessoriGroups, id: 'accessori' },
  };

  // Header sempre chiaro (glass ivory) per leggibilità su tutte le pagine
  const isTransparent = false;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-enter ease-out ${
        isTransparent ? 'bg-transparent' : 'glass border-b border-line'
      }`}
      role="banner"
    >
      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-22">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group"
            data-testid="logo-link"
            aria-label="Ciro Gallotti — Home"
          >
            <img
              src="/logo.png"
              alt="Ciro Gallotti — L'Arte del Gioiello"
              className="h-14 md:h-16 w-auto object-contain transition-transform duration-hover group-hover:scale-[1.03]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10" aria-label="Navigazione principale">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              const linkClass = `relative text-eyebrow line-animation pb-1 transition-colors duration-hover ${
                isActive ? 'text-bordeaux' : 'text-ink/70 hover:text-bordeaux'
              }`;

              // Collezione → menu a tendina con le categorie
              if (link.href === '/collezione') {
                return (
                  <div key={link.href} className="relative group">
                    <Link
                      to={link.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={`${linkClass} inline-flex items-center gap-1`}
                      data-testid="nav-collezione"
                    >
                      {link.label}
                      <ChevronDown className="w-3.5 h-3.5 transition-transform duration-hover group-hover:rotate-180" />
                    </Link>
                    {/* Dropdown */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-enter ease-out z-50">
                      <div className="min-w-[200px] bg-ivory border border-line rounded-xl shadow-lg shadow-ink/5 py-2" data-testid="collezione-dropdown">
                        <Link
                          to="/collezione"
                          className="block px-5 py-2.5 text-sm text-ink/70 hover:text-bordeaux hover:bg-secondary transition-colors duration-hover"
                          data-testid="dropdown-tutti"
                        >
                          Tutta la Collezione
                        </Link>
                        <div className="h-px bg-line my-1.5 mx-3" />
                        {CATEGORIES.map((cat) => (
                          <Link
                            key={cat}
                            to={`/collezione?category=${encodeURIComponent(cat)}`}
                            className="block px-5 py-2.5 text-sm text-ink/70 hover:text-bordeaux hover:bg-secondary transition-colors duration-hover"
                            data-testid={`dropdown-cat-${cat}`}
                          >
                            {cat}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              // Argento / Accessori → collezioni dedicate con tendina sottogruppi
              if (subMenus[link.href]) {
                const { subs, id } = subMenus[link.href];
                return (
                  <div key={link.href} className="relative group">
                    <Link
                      to={link.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={`${linkClass} inline-flex items-center gap-1`}
                      data-testid={`nav-${id}`}
                    >
                      {link.label}
                      <ChevronDown className="w-3.5 h-3.5 transition-transform duration-hover group-hover:rotate-180" />
                    </Link>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-enter ease-out z-50">
                      <div className="min-w-[200px] bg-ivory border border-line rounded-xl shadow-lg shadow-ink/5 py-2" data-testid={`${id}-dropdown`}>
                        <Link
                          to={link.href}
                          className="block px-5 py-2.5 text-sm text-ink/70 hover:text-bordeaux hover:bg-secondary transition-colors duration-hover"
                          data-testid={`dropdown-${id}-tutti`}
                        >
                          {id === 'accessori' ? 'Tutti gli Accessori' : 'Tutta la Collezione'}
                        </Link>
                        <div className="h-px bg-line my-1.5 mx-3" />
                        {subs.map((sub) => (
                          <Link
                            key={sub}
                            to={`${link.href}?sub=${encodeURIComponent(sub)}`}
                            className="block px-5 py-2.5 text-sm text-ink/70 hover:text-bordeaux hover:bg-secondary transition-colors duration-hover"
                            data-testid={`dropdown-${id}-${sub}`}
                          >
                            {sub}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.href}
                  to={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={linkClass}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Prenota Appuntamento (desktop) */}
            <Link to="/appuntamento" className="hidden lg:block" data-testid="header-appointment-btn">
              <Button variant="accent" className="rounded-full">
                Prenota Appuntamento
              </Button>
            </Link>

            {/* Wishlist */}
            <Link
              to="/preferiti"
              className="relative group hidden sm:block"
              data-testid="wishlist-link"
              aria-label={`Preferiti, ${wishlistCount} ${wishlistCount === 1 ? 'articolo' : 'articoli'}`}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-hover ${
                isTransparent
                  ? 'bg-ivory/10 hover:bg-ivory/20'
                  : 'border border-line hover:border-ink hover:bg-ink hover:text-ivory'
              }`}>
                <Heart className={`w-[18px] h-[18px] ${isTransparent ? 'text-ivory' : 'text-ink group-hover:text-ivory'}`} />
              </div>
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-bordeaux text-ivory text-[10px] font-medium flex items-center justify-center rounded-full animate-scale-in">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link
              to="/carrello"
              className="relative group"
              data-testid="cart-link"
              aria-label={`Carrello, ${cartItemsCount} ${cartItemsCount === 1 ? 'articolo' : 'articoli'}`}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-hover ${
                isTransparent
                  ? 'bg-ivory/10 hover:bg-ivory/20'
                  : 'border border-line hover:border-ink hover:bg-ink hover:text-ivory'
              }`}>
                <ShoppingBag className={`w-[18px] h-[18px] ${isTransparent ? 'text-ivory' : 'text-ink group-hover:text-ivory'}`} />
              </div>
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-bordeaux text-ivory text-[10px] font-medium flex items-center justify-center rounded-full animate-scale-in">
                  {cartItemsCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`w-11 h-11 rounded-full ${
                    isTransparent
                      ? 'bg-ivory/10 text-ivory hover:bg-ivory/20'
                      : 'border border-line text-ink hover:border-ink hover:bg-ink hover:text-ivory'
                  }`}
                  data-testid="mobile-menu-trigger"
                  aria-label="Apri menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full bg-ink border-none p-0">
                <div className="flex flex-col h-full p-8 md:p-10">
                  <div className="flex justify-between items-center mb-12">
                    <span className="font-display text-h4 text-ivory">Menu</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsOpen(false)}
                      className="w-11 h-11 rounded-sm bg-ivory/10 text-ivory hover:bg-ivory/20"
                      data-testid="mobile-menu-close"
                      aria-label="Chiudi menu"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <nav className="flex flex-col gap-6" aria-label="Navigazione mobile">
                    {navLinks.map((link, i) => (
                      <div key={link.href}>
                        <Link
                          to={link.href}
                          onClick={() => setIsOpen(false)}
                          className="font-display text-3xl md:text-4xl text-ivory hover:text-bordeaux transition-colors duration-hover animate-slide-up block"
                          style={{ animationDelay: `${i * 60}ms` }}
                          data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                        >
                          {link.label}
                        </Link>
                        {link.href === '/collezione' && (
                          <div className="mt-3 flex flex-col gap-2 pl-1" data-testid="mobile-categories">
                            {CATEGORIES.map((cat) => {
                              if (cat === 'Orologi') {
                                return (
                                  <div key={cat}>
                                    <div className="flex items-center gap-2">
                                      <Link
                                        to="/collezione?category=Orologi"
                                        onClick={() => setIsOpen(false)}
                                        className="text-ivory hover:text-bordeaux transition-colors duration-hover text-lg"
                                        data-testid="mobile-cat-Orologi"
                                      >
                                        Orologi
                                      </Link>
                                      <button
                                        type="button"
                                        onClick={() => setWatchOpen((v) => !v)}
                                        className="text-ivory hover:text-bordeaux transition-colors duration-hover p-1"
                                        aria-label="Mostra i brand di orologi"
                                        aria-expanded={watchOpen}
                                        data-testid="mobile-watch-toggle"
                                      >
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-hover ${watchOpen ? 'rotate-180' : ''}`} />
                                      </button>
                                    </div>
                                    {watchOpen && (
                                      <div className="mt-2 flex flex-col gap-2 pl-4 border-l border-ivory/15" data-testid="mobile-watch-brands">
                                        {WATCH_BRANDS.map((b) => (
                                          <Link
                                            key={b}
                                            to={`/collezione?category=Orologi&brand=${encodeURIComponent(b)}`}
                                            onClick={() => setIsOpen(false)}
                                            className="text-ivory hover:text-bordeaux transition-colors duration-hover text-base"
                                            data-testid={`mobile-brand-${b}`}
                                          >
                                            {b}
                                          </Link>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <Link
                                  key={cat}
                                  to={`/collezione?category=${encodeURIComponent(cat)}`}
                                  onClick={() => setIsOpen(false)}
                                  className="text-ivory hover:text-bordeaux transition-colors duration-hover text-lg"
                                  data-testid={`mobile-cat-${cat}`}
                                >
                                  {cat}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                        {subMenus[link.href] && (
                          <div className="mt-3 flex flex-col gap-2 pl-1" data-testid={`mobile-${subMenus[link.href].id}-subs`}>
                            {subMenus[link.href].subs.map((sub) => (
                              <Link
                                key={sub}
                                to={`${link.href}?sub=${encodeURIComponent(sub)}`}
                                onClick={() => setIsOpen(false)}
                                className="text-ivory hover:text-bordeaux transition-colors duration-hover text-lg"
                                data-testid={`mobile-${subMenus[link.href].id}-${sub}`}
                              >
                                {sub}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>

                  <div className="mt-auto pt-10 border-t border-ivory/10">
                    <Link
                      to="/preferiti"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-4 text-ivory/60 hover:text-ivory transition-colors duration-hover mb-5"
                      data-testid="mobile-wishlist-link"
                    >
                      <div className="w-11 h-11 rounded-sm bg-ivory/10 flex items-center justify-center">
                        <Heart className="w-5 h-5" />
                      </div>
                      <span>Preferiti ({wishlistCount})</span>
                    </Link>
                    <Link
                      to="/carrello"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-4 text-ivory/60 hover:text-ivory transition-colors duration-hover"
                      data-testid="mobile-cart-link"
                    >
                      <div className="w-11 h-11 rounded-sm bg-ivory/10 flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5" />
                      </div>
                      <span>Carrello ({cartItemsCount})</span>
                    </Link>

                    <div className="mt-8 space-y-1 text-ivory/40 text-small">
                      <p>Via Chiaia 138, Napoli</p>
                      <p>+39 333 645 0082</p>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
